import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AdminAuthProvider, useAdminAuth, hasRole } from './auth';
import { AttentionProvider } from './attention';
import { RangeProvider } from './range';
import AppShell from './components/AppShell';
import SectionLayout from './components/SectionLayout';

import Login from './pages/Login';
import HomePage from './pages/HomePage';

import GrowthOverviewPage from './pages/growth/GrowthOverviewPage';
import FunnelPage from './pages/growth/FunnelPage';
import CohortsPage from './pages/growth/CohortsPage';
import PeoplePage from './pages/growth/PeoplePage';

import BrandsPage from './pages/catalog/BrandsPage';
import ApplicationsPage from './pages/catalog/ApplicationsPage';
import CatalogProductsPage from './pages/catalog/ProductsPage';
import CrawlPage from './pages/catalog/CrawlPage';

import StylistPage from './pages/ai/StylistPage';
import LlmUsagePage from './pages/ai/LlmUsagePage';
import TryOnPage from './pages/ai/TryOnPage';
import TranscriptsPage from './pages/ai/TranscriptsPage';

import HealthPage from './pages/system/HealthPage';
import FlagsPage from './pages/system/FlagsPage';
import AuditPage from './pages/system/AuditPage';
import SystemStaffPage from './pages/system/StaffPage';

import BrandDetailPage from './pages/entity/BrandDetailPage';
import ProductDetailPage from './pages/entity/ProductDetailPage';
import UserDetailPage from './pages/entity/UserDetailPage';

/**
 * Five sections, three entity pages, and redirects from the old eleven tabs.
 *
 * The role gates here decide where someone *lands*, not what they may do —
 * the API refuses regardless of what this file says. Their only job is to
 * keep a direct-URL visit or a stale bookmark from dead-ending on a raw 403.
 */
function Guarded({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page text-sm text-ink-muted">
        Loading…
      </div>
    );
  }
  if (!admin) return <Navigate to="/login" replace />;
  return children;
}

function RoleGate({ roles, fallback, children }) {
  const { admin } = useAdminAuth();
  return hasRole(admin, ...roles) ? children : <Navigate to={fallback} replace />;
}

const OwnerOnly = ({ children, fallback = '/' }) => (
  <RoleGate roles={['owner']} fallback={fallback}>{children}</RoleGate>
);

// Drilling into one person is an operator/support/owner action (product doc
// Section 7). An analyst arriving by direct link lands on Growth, not an error.
const DrillDownGated = ({ children }) => (
  <RoleGate roles={['operator', 'support', 'owner']} fallback="/growth">{children}</RoleGate>
);

const OperatorOnly = ({ children, fallback = '/catalog' }) => (
  <RoleGate roles={['operator', 'owner']} fallback={fallback}>{children}</RoleGate>
);

/** `/users/:id` → `/user/:id`, keeping the id. */
function LegacyUserRedirect() {
  const { userId } = useParams();
  return <Navigate to={`/user/${encodeURIComponent(userId)}`} replace />;
}

function App() {
  return (
    <AdminAuthProvider>
      <Router>
        {/* RangeProvider needs the router (it reads and writes the query
            string); AttentionProvider needs auth. Both sit above the shell so
            the time control and the nav badges exist on every page. */}
        <RangeProvider>
          <AttentionProvider>
            <Routes>
              <Route path="/login" element={<Login />} />

              <Route path="/" element={<Guarded><AppShell /></Guarded>}>
                <Route index element={<HomePage />} />

                <Route path="growth" element={<SectionLayout section="growth" />}>
                  <Route index element={<GrowthOverviewPage />} />
                  <Route path="funnel" element={<FunnelPage />} />
                  <Route path="cohorts" element={<CohortsPage />} />
                  <Route path="people" element={<DrillDownGated><PeoplePage /></DrillDownGated>} />
                </Route>

                <Route path="catalog" element={<SectionLayout section="catalog" />}>
                  <Route index element={<BrandsPage />} />
                  <Route path="applications" element={<OperatorOnly><ApplicationsPage /></OperatorOnly>} />
                  <Route path="products" element={<CatalogProductsPage />} />
                  <Route path="crawl" element={<CrawlPage />} />
                </Route>

                <Route path="ai" element={<SectionLayout section="ai" />}>
                  <Route index element={<StylistPage />} />
                  <Route path="usage" element={<LlmUsagePage />} />
                  <Route path="tryon" element={<TryOnPage />} />
                  {/* Transcripts are the one user-level view gated to owner
                      alone — the most sensitive data in the panel. */}
                  <Route path="transcripts" element={<OwnerOnly fallback="/ai"><TranscriptsPage /></OwnerOnly>} />
                </Route>

                <Route path="system" element={<SectionLayout section="system" />}>
                  <Route index element={<HealthPage />} />
                  <Route path="flags" element={<FlagsPage />} />
                  <Route path="audit" element={<AuditPage />} />
                  <Route path="staff" element={<OwnerOnly fallback="/system"><SystemStaffPage /></OwnerOnly>} />
                </Route>

                {/* Entity pages sit outside the sections deliberately: a brand
                    is reached from Catalog, from Funnel, from Crawl and from
                    search, so nesting it under any one of them would put a
                    misleading tab strip above it. */}
                <Route path="brand/:brandId" element={<BrandDetailPage />} />
                <Route path="product/:sku" element={<ProductDetailPage />} />
                <Route path="user/:userId" element={<DrillDownGated><UserDetailPage /></DrillDownGated>} />

                {/* Old URLs. Bookmarks and Slack links from the previous
                    eleven-tab panel keep working rather than silently landing
                    on Home, which reads as "the page was deleted". */}
                <Route path="users" element={<Navigate to="/growth" replace />} />
                <Route path="users/:userId" element={<LegacyUserRedirect />} />
                <Route path="brands" element={<Navigate to="/catalog" replace />} />
                <Route path="partners" element={<Navigate to="/catalog/applications" replace />} />
                <Route path="products" element={<Navigate to="/catalog/products" replace />} />
                <Route path="crawl" element={<Navigate to="/catalog/crawl" replace />} />
                <Route path="tryon" element={<Navigate to="/ai/tryon" replace />} />
                <Route path="mahi" element={<Navigate to="/ai" replace />} />
                <Route path="conversations" element={<Navigate to="/ai/transcripts" replace />} />
                <Route path="flags" element={<Navigate to="/system/flags" replace />} />
                <Route path="audit" element={<Navigate to="/system/audit" replace />} />
                <Route path="staff" element={<Navigate to="/system/staff" replace />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AttentionProvider>
        </RangeProvider>
        <Toaster position="top-right" />
      </Router>
    </AdminAuthProvider>
  );
}

export default App;
